import React from 'react';
import {
  InputBox,
  InputWrapper,
  InputSize,
  InputLabel,
  InputContent,
  ChevronContainer,
  InputContainer,
} from './Input.styles';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import Typography from 'src/app/atoms/Typography/Typography';
import { BreakpointKey } from 'src/app/styles/breakpoints';
import {
  FontSizePropKeys,
  FontFamilyPropKeys,
  FontWeightPropKeys,
  LineHeightPropKeys,
} from 'src/app/styles/typography';
import Chevron from 'src/app/icons/Monospaced/Chevron/Chevron';

export interface SelectOption {
  value: string;
  label: string;
}

export interface InputProps {
  value: string;
  onChange?: (value: string) => void;
  size?: InputSize;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  topRightElement?: React.ReactNode;
  disabled?: boolean;
  actionable?: boolean;
  fontSize?: FontSizePropKeys;
  fontFamily?: FontFamilyPropKeys;
  fontWeight?: FontWeightPropKeys;
  lineHeight?: LineHeightPropKeys;
  placeholder?: string;
  className?: string;
  label?: string;
  type?: 'text' | 'number' | 'select';
  required?: boolean;
  error?: string;
  max?: number;
  min?: number;
  labelBreakpoint?: BreakpointKey;
  options?: SelectOption[];
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const Input = ({
  value,
  onChange,
  size = 'md',
  leftElement,
  rightElement,
  disabled = false,
  actionable = true,
  fontSize = 'font-size-text-md',
  fontFamily = 'font-family-body',
  fontWeight = 'font-weight-regular',
  lineHeight = 'line-height-text-md',
  placeholder,
  className,
  label,
  required = false,
  type = 'text',
  error,
  topRightElement,
  max,
  min,
  labelBreakpoint,
  options = [],
  onKeyDown,
}: InputProps): JSX.Element => {
  const validateMaxAndMin = (value: string): boolean => {
    if (type !== 'number') return true;

    const numberValue = Number(value);

    if (max !== undefined && numberValue > max) return false;
    if (min !== undefined && numberValue < min) return false;

    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!disabled && actionable) {
      const isValid = validateMaxAndMin(e.target.value);

      if (isValid) onChange?.(e.target.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!actionable) {
      e.preventDefault();
    } else {
      onKeyDown?.(e);
    }
  };

  const renderLabelContent = () => (
    <>
      {label && (
        <InputLabel>
          {label}
          {required && <span>*</span>}
        </InputLabel>
      )}
      {topRightElement && (
        <Stack
          align='center'
          justify='center'
        >
          {topRightElement}
        </Stack>
      )}
    </>
  );

  const renderInput = () => {
    if (type === 'select') {
      return (
        <>
          <select
            value={value}
            onChange={handleChange}
            disabled={disabled}
            required={required}
            tabIndex={actionable ? 0 : -1}
          >
            {placeholder && (
              <option
                value=''
                disabled
              >
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
          <ChevronContainer>
            <Chevron
              orientation='down'
              color='var(--gray-light-400)'
              width={20}
            />
          </ChevronContainer>
        </>
      );
    }

    return (
      <input
        type={type}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={!actionable}
        tabIndex={actionable ? 0 : -1}
        required={required}
      />
    );
  };

  return (
    <InputContainer
      direction='column'
      gap='spacing-sm'
      className={className}
    >
      {(label || topRightElement) && (
        <Stack
          direction='row'
          align='center'
          justify='space-between'
          hideOn={labelBreakpoint}
        >
          {renderLabelContent()}
        </Stack>
      )}
      {labelBreakpoint && (
        <Stack
          direction='column'
          align='flex-start'
          justify='flex-start'
          gap='spacing-xs'
          showOn={labelBreakpoint}
        >
          {renderLabelContent()}
        </Stack>
      )}
      <InputBox
        $size={size}
        $disabled={disabled}
        $actionable={actionable}
        $error={!!error}
      >
        <InputContent>
          {leftElement && (
            <Stack
              align='center'
              justify='center'
              marginRight='spacing-lg'
            >
              {leftElement}
            </Stack>
          )}
          <InputWrapper
            $size={size}
            $disabled={disabled}
            $actionable={actionable}
            $hasLeftElement={!!leftElement}
            $hasRightElement={!!rightElement}
            $fontSize={fontSize}
            $fontFamily={fontFamily}
            $fontWeight={fontWeight}
            $lineHeight={lineHeight}
          >
            {renderInput()}
          </InputWrapper>
          {rightElement && (
            <Stack
              align='center'
              justify='center'
              marginLeft='spacing-lg'
            >
              {rightElement}
            </Stack>
          )}
        </InputContent>
      </InputBox>
      {error && (
        <Typography
          type='paragraph'
          size='sm'
          color='text-error-primary'
          fontWeight='font-weight-regular'
        >
          {error}
        </Typography>
      )}
    </InputContainer>
  );
};

export default Input;
